import {
  GetTaxCommissionIncomeDetails,
  SetApplicationFlag,
} from '@/apis/family_application/requests'
import { LogUserActivity } from '@/apis/users/requests'
import AppAlertComponent from '@/app/common/components/AppAlertComponent/AppAlertComponent'
import { Button } from '@/app/common/components/Button'
import LoadingCard from '@/app/common/components/LoadingCard'
import { FamilyApplicationData } from '@/layouts/FamilyApplicationLayout'
import { Label } from '@/shad/ui/label'
import { RadioGroup, RadioGroupItem } from '@/shad/ui/radio-group'
import { selectProgramID } from '@/storage/program_settings/programSettingsSlice'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useOutletContext } from 'react-router-dom'
import { z } from 'zod'
import { LOG_TYPES } from '../../constants'
import FamilyWizardLayout from '../../FamilyWizardLayout'
import { hasTaxCommissionErrors } from './tax-commission.utils'

const FormSchema = z.object({
  informationCorrect: z.enum(['yes', 'no'], {
    required_error: 'error.required',
  }),
})

type FormValues = z.infer<typeof FormSchema>

type Props = {
  onInformationCorrect: () => void
  onInformationIncorrect: () => void
  onValidationErrorContinueClick: () => void
}

function TaxCommissionVerificationPage(props: Props) {
  const {
    onInformationCorrect,
    onInformationIncorrect,
    onValidationErrorContinueClick,
  } = props
  const { t } = useTranslation()
  const { familyApplication } = useOutletContext<FamilyApplicationData>()
  const programId = useSelector(selectProgramID)
  const [userHasConfirmedResult, setUserHasConfirmedResult] = useState(false)

  const {
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      informationCorrect: undefined,
    },
  })

  const hasLoggedSuccessView = useRef(false);
  useEffect(() => {
    if (!hasLoggedSuccessView.current && userHasConfirmedResult) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'Tax Comission Succesfully verified',
      })
      hasLoggedSuccessView.current = true;
    }
  }, [userHasConfirmedResult]) 

  const { status, data, mutate } = useMutation({
    onError: async () => {
      await SetApplicationFlag({
        application_id: familyApplication.application_id,
        flag: 'tax_commission_error',
        global_application_step_code: 'tax_commission',
      })
    },
    onSettled: async (data) => {
      if (data?.error) {
        await SetApplicationFlag({
          application_id: familyApplication.application_id,
          flag: 'tax_commission_returned_data_does_not_match',
          global_application_step_code: 'tax_commission',
        })
      }
    },
    mutationFn: async () =>
      GetTaxCommissionIncomeDetails({
        application_id: familyApplication.application_id,
        program_id: programId,
      }),
  })
  useEffect(() => {
    mutate()
  }, [])

  const onSubmit = async (data: FormValues) => {
    try {
      if (data.informationCorrect === 'yes') {
        setUserHasConfirmedResult(true)
      } else {
        await SetApplicationFlag({
          application_id: familyApplication.application_id,
          flag: 'tax_commission_applicant_attested_incorrect_data',
          global_application_step_code: 'tax_commission',
        })
        onInformationIncorrect()
      }
    } catch (error) {
      console.error('Error submitting form:', error)
    }
  }

  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  })

  const logTaxCommissionError = () => {
    LogUserActivity({
      log_type: LOG_TYPES.VIEW_PAGE,
      suffix_text: `Tax comission unable to verify`,
    })
  }

  if (userHasConfirmedResult) {
    return (
      <AppAlertComponent
        variant="success"
        title={t('taxVerification.success.title')}
        text={t('taxVerification.success.text')}
      >
        <Button onClick={onInformationCorrect}>{t('continue')}</Button>
      </AppAlertComponent>
    )
  }

  if (status === 'pending') {
    return (
      <LoadingCard isLoading={true}>{t('taxVerification.loading')}</LoadingCard>
    )
  }

  if (status === 'error' || !data?.data) {
    return (
      <div>
        <AppAlertComponent
          title={t('taxVerification.error.title')}
          variant="error"
          text={t('taxVerification.error.text')}
        >
          <Button
            onClick={() => {
              logTaxCommissionError()
              onValidationErrorContinueClick()
            }}
          >
            {t('continue')}
          </Button>
        </AppAlertComponent>
      </div>
    )
  }

  if (hasTaxCommissionErrors(data.data)) {
    return (
      <AppAlertComponent
        title={t('taxVerification.error.title')}
        variant="warning"
        text={data.data.map((person) => {
          return person.errors.map((error) => (
            <li className="list-disc my-1">{error.message}</li>
          ))
        })}
      >
        <Button
          onClick={() => {
            logTaxCommissionError()
            onValidationErrorContinueClick()
          }}
        >
          {t('continue')}
        </Button>
      </AppAlertComponent>
    )
  }

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        {t('taxVerification.header')}
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content>
        <div className="text-gray-800 font-medium text-lg text-center mb-4">
          {data.data.map((item) => {
            return t('taxVerification.incomeQuestion', {
              amount: currencyFormatter.format(item.FedAdjustedGrossIncome),
            })
          })}
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <RadioGroup
            className="space-y-4"
            defaultValue={undefined}
            onValueChange={(value) => {
              const values = FormSchema.safeParse({
                informationCorrect: value,
              })

              if (values.success) {
                setValue('informationCorrect', values.data.informationCorrect, {
                  shouldValidate: true,
                })
              } else {
                console.error('Error parsing form values:', values.error)
              }
            }}
          >
            <div className="flex space-x-2 rounded-lg py-2 px-2 hover:bg-gray-100">
              <RadioGroupItem value="yes" id="consent-yes" />
              <Label htmlFor="consent-yes" className="flex flex-col flex-1">
                <span className="font-medium mb-1 text-md">{t('yes')}</span>
                <span className="text-gray-700 font-normal">
                  {t('taxVerification.responses.correct')}
                </span>
              </Label>
            </div>

            <div className="flex space-x-2 rounded-lg py-2 px-2 hover:bg-gray-100">
              <RadioGroupItem value="no" id="consent-no" />
              <Label htmlFor="consent-no" className="flex flex-col flex-1">
                <span className="font-medium mb-1 text-md">{t('no')}</span>
                <span className="text-gray-700 font-normal">
                  {t('taxVerification.responses.incorrect')}
                </span>
              </Label>
            </div>
          </RadioGroup>

          {errors.informationCorrect && (
            <p className="text-red-500 text-sm">{t('required')}</p>
          )}

          <FamilyWizardLayout.ActionBar>
            <Button>{t('continue')}</Button>
          </FamilyWizardLayout.ActionBar>
        </form>
      </FamilyWizardLayout.Content>
    </FamilyWizardLayout>
  )
}

export default TaxCommissionVerificationPage
